/**
 * A mecânica das respostas rápidas no compositor, pura (sem React, sem `@/`):
 * quando a `/` abre a lista, o que o teclado faz com ela aberta e como o texto
 * escolhido entra na caixa. Testada na API (apps/api/src/shared/
 * quick-replies.test.ts) — o web não tem executor de testes.
 *
 * A regra que não se negocia: com a lista aberta, Enter ESCOLHE (ou fecha a
 * lista vazia) — nunca chega ao envio. Escolher só põe o texto na caixa; quem
 * envia é o atendente, depois de revisar.
 */

/** o que se digita depois da `/` — o tamanho máximo de um atalho */
export const SLASH_QUERY_MAX = 32;

export interface SlashTrigger {
  /** posição da `/` no texto */
  start: number;
  /** o cursor: o trecho [start, end) é o que a resposta substitui */
  end: number;
  /** o que vem depois da `/`, sem ela */
  query: string;
}

const SLASH_BEFORE_CARET = new RegExp(`(^|\\s)\\/([^\\s/]{0,${SLASH_QUERY_MAX}})$`);

/**
 * A `/` que abre a lista: no início da mensagem ou logo depois de espaço ou
 * quebra de linha, com o cursor no fim do que vem depois dela, sem espaço no
 * meio. "http://x", "1/2" e "a/b" não abrem nada.
 */
export function findSlashTrigger(text: string, caret: number): SlashTrigger | null {
  if (caret < 1 || caret > text.length) return null;
  const m = SLASH_BEFORE_CARET.exec(text.slice(0, caret));
  if (!m) return null;
  const query = m[2] ?? '';
  return { start: caret - query.length - 1, end: caret, query };
}

/** Troca o `/consulta` pelo texto da resposta; o cursor vai para o fim dele. */
export function replaceSlashTrigger(
  text: string,
  trigger: Pick<SlashTrigger, 'start' | 'end'>,
  body: string
): { text: string; caret: number } {
  return {
    text: text.slice(0, trigger.start) + body + text.slice(trigger.end),
    caret: trigger.start + body.length,
  };
}

/**
 * Insere pelo botão, na seleção da caixa (ou no cursor). Colado numa palavra,
 * entra um espaço antes — "Olá" + "Segue o link" não vira "OláSegue".
 */
export function insertAtSelection(
  text: string,
  selection: { start: number; end: number },
  body: string
): { text: string; caret: number } {
  const start = Math.max(0, Math.min(selection.start, text.length));
  const end = Math.max(start, Math.min(selection.end, text.length));
  const before = text.slice(0, start);
  const glue = before !== '' && !/\s$/.test(before) ? ' ' : '';
  const inserted = glue + body;
  return { text: before + inserted + text.slice(end), caret: start + inserted.length };
}

export type QuickReplyKeyAction = 'up' | 'down' | 'choose' | 'close' | 'pass';

/**
 * O que uma tecla faz com a lista ABERTA. `pass` devolve a tecla ao compositor
 * (é o caminho do Enter que envia) — por isso Enter sem Shift nunca é `pass`
 * aqui. Shift+Enter quebra a linha, como sempre, e a quebra fecha a lista.
 * Durante a composição de um acento (IME) nada é interceptado.
 */
export function quickReplyKeyAction(
  e: { key: string; shiftKey?: boolean; isComposing?: boolean },
  open: boolean
): QuickReplyKeyAction {
  if (!open || e.isComposing) return 'pass';
  switch (e.key) {
    case 'ArrowDown':
      return 'down';
    case 'ArrowUp':
      return 'up';
    case 'Enter':
      return e.shiftKey ? 'pass' : 'choose';
    case 'Tab':
      return e.shiftKey ? 'pass' : 'choose';
    case 'Escape':
      return 'close';
    default:
      return 'pass';
  }
}

/** ↑ ↓ com volta: do último para o primeiro e vice-versa; lista vazia é 0 */
export function moveHighlight(index: number, delta: 1 | -1, count: number): number {
  if (count <= 0) return 0;
  return (((index + delta) % count) + count) % count;
}
